import * as Sentry from '@sentry/react';
import {
  CameraOptions,
  CameraType,
  PointcloudDisplay,
  VizScene,
  RangeRGBData,
  DataMessage,
  resolveHoveredPoint,
} from '@ouster/webviz';
import {
  Box,
  Flex,
  Grid,
  chakra,
  TwoDImagePanel,
  PointCloudGlobalSettings,
  PointCloudSensorsSettings,
  ImageContainerState,
  PointCloudHoverTooltip,
  Spinner,
} from '@ouster/webviz-react-components';

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
  type RefObject,
} from 'react';
import { getVizDebugConfig } from 'src/utils/vizDebugConfig';

import { useStoreState, useStoreActions } from '../reducers';
import { applyPointCloudRgbAutoExposure } from '../reducers/pointCloudRgbAutoExposure';
import type { TwoDImageState } from '../reducers/vizImage';
import {
  selectSelectedImagePanelSn,
  selectSelectedSensorImageSettings,
} from '../reducers/vizScene';
import { DriveInfo, HoveredPointData, SDKSensorInfoAugmented } from '../types';

const SDK_INIT_YAW_DEG = 0;
const SDK_INIT_PITCH_DEG = -45;
const SDK_INIT_LOG_DISTANCE = 0;
const SDK_INIT_FOV_DEG = 90;
const SDK_INIT_ORBIT_TARGET: [number, number, number] = [0, 0, 0];
const RAW_RGB_SUFFIX = ' RAW';

export const addRawRgbHoverChannels = (
  msg: DataMessage,
  source: DataMessage
): DataMessage => {
  const { channelFields: sourceChannelFields } = source;
  const {
    channelFields: msgChannelFields,
    availableChannels: msgAvailableChannels,
    rgbChannels: msgRgbChannels,
  } = msg;
  const sourceRgbChannels = source.rgbChannels ?? [];
  if (sourceRgbChannels.length === 0) {
    return msg;
  }

  let channelFields = msgChannelFields;
  let availableChannels = [...msgAvailableChannels] as string[];
  let rgbChannels = msgRgbChannels ?? sourceRgbChannels;
  let changed = false;

  sourceRgbChannels.forEach((channelName) => {
    const rawChannelName = `${channelName}${RAW_RGB_SUFFIX}`;
    const rawField =
      sourceChannelFields[rawChannelName] ?? sourceChannelFields[channelName];

    if (!rawField) {
      return;
    }

    if (channelFields[rawChannelName] !== rawField) {
      channelFields = {
        ...channelFields,
        [rawChannelName]: rawField,
      };
      changed = true;
    }

    if (!availableChannels.includes(rawChannelName)) {
      availableChannels = [...availableChannels, rawChannelName];
      changed = true;
    }

    if (!rgbChannels.includes(rawChannelName)) {
      rgbChannels = [...rgbChannels, rawChannelName];
      changed = true;
    }
  });

  if (!changed) {
    return msg;
  }

  return {
    ...msg,
    channelFields,
    availableChannels,
    rgbChannels,
  };
};

const SDK_LOG_DISTANCE_0_M = 50;
const SDK_LOG_DISTANCE_SCALE = 0.01;
const DEFAULT_VIEW_ASPECT = 16 / 9;

const degToRad = (degrees: number) => (degrees * Math.PI) / 180;

/*
 * SDK camera parity notes:
 * - SDK stores FOV as diagonal angle.
 * - Three.js PerspectiveCamera expects vertical FOV.
 * - We convert SDK diagonal FOV -> vertical FOV and apply it with the runtime
 *   canvas aspect during scene init so first-load framing matches SDK.
 */
export const sdkDiagonalFovToVerticalFov = (
  diagonalFovDeg: number,
  aspect: number
): number => {
  const safeAspect = Number.isFinite(aspect) && aspect > 0 ? aspect : 1;
  const diagonalHalfAngleRad = degToRad(diagonalFovDeg) / 2;
  const scale = Math.tan(diagonalHalfAngleRad);
  const aspectHeight = 1 / Math.sqrt(1 + safeAspect * safeAspect);
  const verticalHalfAngleRad = Math.atan(aspectHeight * scale);
  return (verticalHalfAngleRad * 2 * 180) / Math.PI;
};

export const createSdkLikeInitialPosition = (): [number, number, number] => {
  const yawRad = degToRad(SDK_INIT_YAW_DEG);
  const pitchRad = degToRad(SDK_INIT_PITCH_DEG);
  const distance =
    SDK_LOG_DISTANCE_0_M *
    Math.exp(SDK_INIT_LOG_DISTANCE * SDK_LOG_DISTANCE_SCALE);

  const groundDistance = distance * Math.cos(pitchRad);
  const height = -distance * Math.sin(pitchRad);

  // Match SDK yaw direction so yaw=0 does not require a manual 180 deg rotation.
  const x = -Math.sin(yawRad) * groundDistance;
  const y = -Math.cos(yawRad) * groundDistance;

  return [x, y, height];
};

// Set initial camera using SDK defaults (perspective, yaw/pitch, fov)
const DEFAULT_CAMERA_OPTIONS: CameraOptions = {
  cameraType: CameraType.PERSP,
  position: createSdkLikeInitialPosition(),
  parameters: {
    fov: sdkDiagonalFovToVerticalFov(SDK_INIT_FOV_DEG, DEFAULT_VIEW_ASPECT),
  },
};

type VizSceneInitArgs = {
  vizScene: VizScene | undefined;
  canvasElement: HTMLCanvasElement | null;
  overlayElement: HTMLDivElement | null;
  coloringMode: string;
  createScene?: (
    canvas: HTMLCanvasElement,
    cameraOptions: CameraOptions,
    overlay: HTMLDivElement,
    debugConfig: ReturnType<typeof getVizDebugConfig>
  ) => VizScene;
  createPointcloudDisplay?: (
    scene: VizScene,
    nextColoringMode: string
  ) => PointcloudDisplay;
};

type VizSceneInitResult = {
  scene: VizScene;
  pcDisplay: PointcloudDisplay;
};

export const initializeVizSceneResources = ({
  vizScene,
  canvasElement,
  overlayElement,
  coloringMode,
  createScene = (canvas, cameraOptions, overlay, debugConfig) =>
    new VizScene(canvas, cameraOptions, overlay, debugConfig),
  createPointcloudDisplay = (scene, nextColoringMode) =>
    new PointcloudDisplay(scene, nextColoringMode),
}: VizSceneInitArgs): VizSceneInitResult | null => {
  if (vizScene) {
    return null;
  }

  if (!canvasElement || !overlayElement) {
    throw new Error('Unable to find canvas for pointcloud!');
  }

  const scene = createScene(
    canvasElement,
    DEFAULT_CAMERA_OPTIONS,
    overlayElement,
    getVizDebugConfig()
  );
  scene.setCameraPose(
    DEFAULT_CAMERA_OPTIONS.position ?? createSdkLikeInitialPosition(),
    SDK_INIT_ORBIT_TARGET
  );

  /* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
  if (scene.camera.isPerspectiveCamera) {
    const width = canvasElement.clientWidth;
    const height = canvasElement.clientHeight;
    const aspect = height > 0 ? width / height : DEFAULT_VIEW_ASPECT;
    scene.camera.fov = sdkDiagonalFovToVerticalFov(SDK_INIT_FOV_DEG, aspect);
    scene.camera.updateProjectionMatrix();
  }
  /* eslint-enable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */

  return {
    scene,
    pcDisplay: createPointcloudDisplay(scene, coloringMode),
  };
};

type InitialImagePanelStateArgs = {
  containerElement: HTMLDivElement | null;
  existingContainerState: ImageContainerState | undefined;
  screenHeight: number;
};

export const deriveInitialImagePanelContainerState = ({
  containerElement,
  existingContainerState,
  screenHeight,
}: InitialImagePanelStateArgs): ImageContainerState | null => {
  if (!containerElement?.firstElementChild || existingContainerState) {
    return null;
  }

  const child = containerElement.firstElementChild as HTMLElement;
  const childHeight = child.offsetHeight;
  const childWidth = child.offsetWidth;

  if (childHeight === 0 || childHeight / screenHeight <= 0.2) {
    return null;
  }

  const adjustedRatio = (screenHeight * 0.2) / childHeight;
  const adjustedWidth = Math.round(childWidth * adjustedRatio);

  return {
    top: child.offsetTop,
    left: child.offsetLeft + Math.round((childWidth - adjustedWidth) / 2),
    width: adjustedWidth,
  };
};

type DataMessageHoverRefreshArgs = {
  messages: DataMessage[];
  processDataMessages: (messages: DataMessage[]) => DataMessage[];
  currentDataMessageRef: RefObject<DataMessage[] | null>;
  lastMousePositionRef: RefObject<{
    clientX: number;
    clientY: number;
  } | null>;
  hoverModeRef: RefObject<boolean>;
  pcDisplayRef: RefObject<HoverActiveDisplay | undefined>;
  computeHoverAtClientPos: (clientX: number, clientY: number) => void;
  requestAnimationFrameFn?: typeof requestAnimationFrame;
};

export const updateHoverStateAfterDataMessages = ({
  messages,
  processDataMessages,
  currentDataMessageRef,
  lastMousePositionRef,
  hoverModeRef,
  pcDisplayRef,
  computeHoverAtClientPos,
  requestAnimationFrameFn = requestAnimationFrame,
}: DataMessageHoverRefreshArgs): void => {
  currentDataMessageRef.current = processDataMessages(messages);

  const lastPos = lastMousePositionRef.current;
  if (!lastPos || !hoverModeRef.current || !pcDisplayRef.current) {
    return;
  }

  requestAnimationFrameFn(() => {
    if (!hoverModeRef.current || !pcDisplayRef.current) return;
    pcDisplayRef.current.setHoverActive(true);
    computeHoverAtClientPos(lastPos.clientX, lastPos.clientY);
  });
};

type PointCloudExtrinsicsArgs = {
  pcDisplay:
    | (Pick<PointcloudDisplay, 'extrinsics'> & HoverActiveDisplay)
    | undefined;
  flipped: boolean;
  vizScene?: Pick<VizScene, 'render'>;
};

export const updatePointCloudExtrinsics = ({
  pcDisplay,
  flipped,
  vizScene,
}: PointCloudExtrinsicsArgs): void => {
  if (!pcDisplay) return;

  pcDisplay.extrinsics = flipped
    ? [1, 0, 0, 0, 0, -1, 0, 0, 0, 0, -1, 0, 0, 0, 0, 1]
    : [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
  vizScene?.render();
};

type CameraTypeSyncScene = {
  camera: {
    position: {
      distanceTo: (target: { x: number; y: number; z: number }) => number;
    };
    zoom?: number;
  };
  controls: {
    target: { x: number; y: number; z: number };
  };
  setCameraType: (isOrthographic: boolean) => void;
};

type CameraTypeSyncArgs = {
  vizScene: CameraTypeSyncScene | undefined;
  isOrthographicCamera: boolean;
  lastDistanceRef: RefObject<number>;
  lastZoomRef: RefObject<number>;
  onCameraShareStateChange?: (state: CameraShareState) => void;
  getCameraShareState?: (scene: CameraTypeSyncScene) => CameraShareState | null;
};

export const syncVizCameraType = ({
  vizScene,
  isOrthographicCamera,
  lastDistanceRef,
  lastZoomRef,
  onCameraShareStateChange,
  getCameraShareState,
}: CameraTypeSyncArgs): void => {
  if (!vizScene) return;

  lastDistanceRef.current = vizScene.camera.position.distanceTo(
    vizScene.controls.target
  );
  lastZoomRef.current =
    typeof vizScene.camera.zoom === 'number' ? vizScene.camera.zoom : 1;

  vizScene.setCameraType(isOrthographicCamera);

  const nextCameraState = getCameraShareState?.(vizScene);
  if (nextCameraState) {
    onCameraShareStateChange?.(nextCameraState);
  }
};

const PAUSED_IDLE_TIMEOUT_MS = 1500;

type RenderLoopScene = Pick<VizScene, 'renderLoop' | 'cancelRenderLoop'>;

type VisibilityDocument = Pick<
  Document,
  'visibilityState' | 'hasFocus' | 'addEventListener' | 'removeEventListener'
>;

type ListenerWindow = Pick<Window, 'addEventListener' | 'removeEventListener'>;
type HoverActiveDisplay = {
  setHoverActive: (active: boolean) => void;
};
type PointCloudFrameDisplay = {
  pointSize: number;
  isVisible: boolean;
  whichReturns: number[];
  channelField: string;
  draw: (message: RangeRGBData[][]) => void;
};
type ResizeAwareDisplay = HoverActiveDisplay & {
  resizePickingFramebuffer: () => void;
};
type ResizeAwareScene = Pick<VizScene, 'render'> & {
  renderer: {
    setPixelRatio: (ratio: number) => void;
  };
};
type MediaQueryLike = Pick<
  MediaQueryList,
  'addEventListener' | 'removeEventListener'
>;
type ResizeLifecycleWindow = Pick<
  Window,
  'addEventListener' | 'removeEventListener' | 'devicePixelRatio'
> & {
  matchMedia?: (query: string) => MediaQueryLike;
};
type PointSizeAwareDisplay = ResizeAwareDisplay & {
  pointSize: number;
  clearPoints: () => void;
  draw: (message: RangeRGBData[][]) => void;
};
type ControlChangeEventTarget = {
  addEventListener: (event: 'change', listener: () => void) => void;
  removeEventListener: (event: 'change', listener: () => void) => void;
  target: { x: number; y: number; z: number };
};
type ZoomAwareCamera = {
  isPerspectiveCamera?: boolean;
  isOrthographicCamera?: boolean;
  zoom?: number;
  position: {
    distanceTo: (target: { x: number; y: number; z: number }) => number;
  };
};
type ControlChangeAwareScene = {
  camera: ZoomAwareCamera;
  controls: ControlChangeEventTarget;
};
type PointerLifecycleEventTarget = Pick<
  Window,
  'addEventListener' | 'removeEventListener'
>;

type PausedRenderLoopControllerArgs = {
  vizScene: RenderLoopScene;
  paused: boolean;
  isPausedLoopStoppedRef: RefObject<boolean>;
  pausedIdleTimeoutRef: RefObject<ReturnType<typeof setTimeout> | undefined>;
  documentTarget?: VisibilityDocument;
  windowTarget?: ListenerWindow;
  idleTimeoutMs?: number;
  setTimeoutFn?: typeof setTimeout;
  clearTimeoutFn?: typeof clearTimeout;
};

export const setupPausedRenderLoopController = ({
  vizScene,
  paused,
  isPausedLoopStoppedRef,
  pausedIdleTimeoutRef,
  documentTarget = document,
  windowTarget = window,
  idleTimeoutMs = PAUSED_IDLE_TIMEOUT_MS,
  setTimeoutFn = setTimeout,
  clearTimeoutFn = clearTimeout,
}: PausedRenderLoopControllerArgs): (() => void) => {
  const startRenderLoop = () => {
    if (!isPausedLoopStoppedRef.current) return;
    vizScene.renderLoop();
    isPausedLoopStoppedRef.current = false;
  };

  const stopRenderLoop = () => {
    if (isPausedLoopStoppedRef.current) return;
    vizScene.cancelRenderLoop();
    isPausedLoopStoppedRef.current = true;
  };

  const clearIdleTimer = () => {
    if (pausedIdleTimeoutRef.current) {
      clearTimeoutFn(pausedIdleTimeoutRef.current);
      pausedIdleTimeoutRef.current = undefined;
    }
  };

  const scheduleIdleStop = () => {
    clearIdleTimer();
    pausedIdleTimeoutRef.current = setTimeoutFn(() => {
      if (
        documentTarget.visibilityState === 'visible' &&
        documentTarget.hasFocus() &&
        paused
      ) {
        stopRenderLoop();
      }
    }, idleTimeoutMs);
  };

  const markActivity = () => {
    if (!paused) return;
    startRenderLoop();
    scheduleIdleStop();
  };

  if (!paused) {
    clearIdleTimer();
    startRenderLoop();
    return () => {
      clearIdleTimer();
    };
  }

  if (
    documentTarget.visibilityState === 'visible' &&
    documentTarget.hasFocus()
  ) {
    scheduleIdleStop();
  }

  const handleVisibilityChange = () => {
    if (documentTarget.visibilityState === 'visible') {
      markActivity();
    } else {
      clearIdleTimer();
      stopRenderLoop();
    }
  };

  documentTarget.addEventListener('visibilitychange', handleVisibilityChange);
  windowTarget.addEventListener('pointermove', markActivity, { passive: true });
  windowTarget.addEventListener('wheel', markActivity, { passive: true });
  windowTarget.addEventListener('keydown', markActivity);

  return () => {
    documentTarget.removeEventListener(
      'visibilitychange',
      handleVisibilityChange
    );
    windowTarget.removeEventListener('pointermove', markActivity);
    windowTarget.removeEventListener('wheel', markActivity);
    windowTarget.removeEventListener('keydown', markActivity);
    clearIdleTimer();
    if (paused) {
      stopRenderLoop();
    }
  };
};

type VizPageProps = {
  driveInfo: DriveInfo;
  pointSize: number;
  pointCloudFlipped: boolean;
  twoDImagesFlipped: boolean;
  isMobileViewport: boolean;
  pointCloudGlobalSettings: PointCloudGlobalSettings;
  pointCloudSensorsSettings: PointCloudSensorsSettings;
  paused: boolean;
  hoverMode: boolean;
  setHoverMode: (mode: boolean) => void;
  showTransitionSpinner?: boolean;
  onVizInitialized?: (ready: boolean) => void;
  onVizReadyChange?: (ready: boolean) => void;
  onCameraShareStateChange?: (state: CameraShareState) => void;
};

type CameraShareState = {
  cameraType: 'orthographic' | 'perspective';
  position: [number, number, number];
  target: [number, number, number];
  zoom: number;
};

export interface VizPageRef {
  setSeekingState: (seeking: boolean) => void;
  getIsDrawn: () => boolean;
  getCameraShareState: () => CameraShareState | null;
  applyCameraShareState: (state: CameraShareState) => void;
  refreshPointCloudLayout: () => void;
  captureFrame: () => string | null;
}

export const cameraShareStateFromScene = (
  scene: VizScene | undefined
): CameraShareState | null => {
  if (!scene) return null;

  const camera = scene.camera as {
    isOrthographicCamera?: boolean;
    position?: { x: number; y: number; z: number };
    zoom?: number;
  };
  const controls = scene.controls as {
    target?: { x: number; y: number; z: number };
  };

  if (!camera?.position || !controls?.target) {
    return null;
  }

  const cameraType = camera.isOrthographicCamera
    ? 'orthographic'
    : 'perspective';
  const fallbackZoom =
    typeof camera.zoom === 'number' && Number.isFinite(camera.zoom)
      ? camera.zoom
      : 1;

  return {
    cameraType,
    position: [camera.position.x, camera.position.y, camera.position.z],
    target: [controls.target.x, controls.target.y, controls.target.z],
    zoom: fallbackZoom,
  };
};

type SelectedImageData = {
  sensorName: string;
  info: TwoDImageState['imageInfo'][string];
  observables: TwoDImageState['imageObservables'];
};

type SelectedImageDataArgs = {
  channelsResolved: boolean;
  selectedImagePanelSn: string | null | undefined;
  imageInfo: TwoDImageState['imageInfo'] | undefined;
  imageObservables: TwoDImageState['imageObservables'] | undefined;
  currentSensorImageSettings: unknown[];
};

export const deriveSelectedImageData = ({
  channelsResolved,
  selectedImagePanelSn,
  imageInfo,
  imageObservables,
  currentSensorImageSettings,
}: SelectedImageDataArgs): SelectedImageData | null => {
  if (
    !channelsResolved ||
    !selectedImagePanelSn ||
    !imageInfo ||
    !imageInfo[selectedImagePanelSn] ||
    !imageObservables ||
    imageObservables.length === 0 ||
    currentSensorImageSettings.length === 0
  ) {
    return null;
  }

  return {
    sensorName: selectedImagePanelSn,
    info: imageInfo[selectedImagePanelSn],
    observables: imageObservables,
  };
};

export const deriveContainerStateForPanel = (
  selectedImageData: SelectedImageData | null,
  imageContainerStateSensor: string | null,
  containerState: ImageContainerState | undefined
): ImageContainerState | undefined =>
  selectedImageData?.sensorName !== undefined &&
  imageContainerStateSensor === selectedImageData.sensorName
    ? containerState
    : undefined;

export const shouldResetImagePanelContainerStateOnMobileViewportTransition = ({
  wasMobileViewport,
  isMobileViewport,
  containerState,
}: {
  wasMobileViewport: boolean;
  isMobileViewport: boolean;
  containerState: ImageContainerState | undefined;
}): boolean =>
  !wasMobileViewport && isMobileViewport && containerState !== undefined;

export const deriveSensorSerialNumbers = (
  metadata: SDKSensorInfoAugmented[] | undefined,
  driveInfo: DriveInfo
): Array<string | undefined> => {
  if (Array.isArray(metadata) && metadata.length > 0) {
    return (metadata as Array<{ productSn?: string }>).reduce<string[]>(
      (acc, sensorMeta) => {
        if (typeof sensorMeta.productSn === 'string') {
          acc.push(sensorMeta.productSn);
        }
        return acc;
      },
      []
    );
  }

  return (
    driveInfo?.sensorInfos?.map(
      (info) => info.sdkProductSerialNumber ?? undefined
    ) ?? []
  );
};

export const deriveVisibleReturnNumbers = (
  sensorSerialNumbers: Array<string | undefined>,
  pointCloudSensorsSettings: PointCloudSensorsSettings | undefined
): number[] => {
  const allVisibleIndices: number[] = [];
  if (!pointCloudSensorsSettings || sensorSerialNumbers.length === 0) {
    return [];
  }

  sensorSerialNumbers.forEach((sn) => {
    if (!sn) return;
    const sensorSettings = pointCloudSensorsSettings[sn];
    if (sensorSettings?.returnsSettings) {
      sensorSettings.returnsSettings.forEach((returnSetting, returnIndex) => {
        if (returnSetting?.visible) {
          allVisibleIndices.push(returnIndex);
        }
      });
    }
  });

  return allVisibleIndices;
};

type HoverLifecycleDocument = Pick<
  Document,
  'visibilityState' | 'addEventListener' | 'removeEventListener'
>;

type HoverLifecycleWindow = Pick<
  Window,
  'addEventListener' | 'removeEventListener'
>;

type HoverLifecycleArgs = {
  documentTarget?: HoverLifecycleDocument;
  windowTarget?: HoverLifecycleWindow;
  hoverModeRef: RefObject<boolean>;
  lastMousePositionRef: RefObject<{
    clientX: number;
    clientY: number;
  } | null>;
  pcDisplayRef: RefObject<HoverActiveDisplay | undefined>;
  clearHoveredPointData: () => void;
};

export const setupHoverWindowLifecycle = ({
  documentTarget = document,
  windowTarget = window,
  hoverModeRef,
  lastMousePositionRef,
  pcDisplayRef,
  clearHoveredPointData,
}: HoverLifecycleArgs): (() => void) => {
  const clearHover = () => {
    clearHoveredPointData();
    lastMousePositionRef.current = null;
    pcDisplayRef.current?.setHoverActive(false);
  };

  const handleMouseOut = (e: MouseEvent) => {
    const toEl = (e.relatedTarget ||
      (e as unknown as { toElement?: Node | null }).toElement) as Node | null;
    if (!toEl) clearHover();
  };

  const handleBlur = () => clearHover();

  const handleVisibilityChange = () => {
    if (documentTarget.visibilityState !== 'visible') clearHover();
  };

  const handleMouseEnter = () => {
    if (hoverModeRef.current) {
      pcDisplayRef.current?.setHoverActive(true);
    }
  };

  windowTarget.addEventListener('blur', handleBlur);
  documentTarget.addEventListener('visibilitychange', handleVisibilityChange);
  documentTarget.addEventListener('mouseout', handleMouseOut);
  windowTarget.addEventListener('mouseenter', handleMouseEnter);

  return () => {
    windowTarget.removeEventListener('blur', handleBlur);
    documentTarget.removeEventListener(
      'visibilitychange',
      handleVisibilityChange
    );
    documentTarget.removeEventListener('mouseout', handleMouseOut);
    windowTarget.removeEventListener('mouseenter', handleMouseEnter);
  };
};

type ResizeLifecycleArgs = {
  vizScene: ResizeAwareScene;
  pcDisplay: ResizeAwareDisplay;
  hoverModeRef: RefObject<boolean>;
  lastMousePositionRef: RefObject<{
    clientX: number;
    clientY: number;
  } | null>;
  computeHoverAtClientPos: (clientX: number, clientY: number) => void;
  windowTarget?: ResizeLifecycleWindow;
  requestAnimationFrameFn?: typeof requestAnimationFrame;
  mediaQueryDprValues?: number[];
};

export const setupResizeDisplayLifecycle = ({
  vizScene,
  pcDisplay,
  hoverModeRef,
  lastMousePositionRef,
  computeHoverAtClientPos,
  windowTarget = window,
  requestAnimationFrameFn = requestAnimationFrame,
  mediaQueryDprValues = [1, 1.5, 2, 2.5, 3],
}: ResizeLifecycleArgs): (() => void) => {
  const handleResize = (): void => {
    requestAnimationFrameFn(() => {
      vizScene.render();
      if (hoverModeRef.current) {
        pcDisplay.resizePickingFramebuffer();
        pcDisplay.setHoverActive(true);
        const lastPos = lastMousePositionRef.current;
        if (lastPos) {
          computeHoverAtClientPos(lastPos.clientX, lastPos.clientY);
        }
      }
    });
  };

  let lastDevicePixelRatio = windowTarget.devicePixelRatio;

  const detectDisplayChange = (): void => {
    const currentDPR = windowTarget.devicePixelRatio;
    if (currentDPR !== lastDevicePixelRatio) {
      lastDevicePixelRatio = currentDPR;

      vizScene.renderer.setPixelRatio(currentDPR);
      vizScene.render();
      if (hoverModeRef.current) {
        pcDisplay.resizePickingFramebuffer();
        pcDisplay.setHoverActive(true);
        const lastPos = lastMousePositionRef.current;
        if (lastPos) {
          computeHoverAtClientPos(lastPos.clientX, lastPos.clientY);
        }
      }
    }
  };

  windowTarget.addEventListener('resize', handleResize);

  if (windowTarget.matchMedia) {
    const mediaQueryLists: MediaQueryLike[] = [];

    mediaQueryDprValues.forEach((dpr) => {
      const mql = windowTarget.matchMedia?.(`(resolution: ${dpr}dppx)`);
      if (!mql) {
        return;
      }

      mql.addEventListener('change', detectDisplayChange);
      mediaQueryLists.push(mql);
    });

    return () => {
      windowTarget.removeEventListener('resize', handleResize);
      mediaQueryLists.forEach((mql) => {
        mql.removeEventListener('change', detectDisplayChange);
      });
    };
  }

  return () => {
    windowTarget.removeEventListener('resize', handleResize);
  };
};

type PointSizeRedrawArgs = {
  pcDisplay: PointSizeAwareDisplay;
  pointSize: number;
  hoverModeRef: RefObject<boolean>;
  lastMousePositionRef: RefObject<{
    clientX: number;
    clientY: number;
  } | null>;
  computeHoverAtClientPos: (clientX: number, clientY: number) => void;
  lastDrawnPcMessage: RangeRGBData[][] | null | undefined;
  requestAnimationFrameFn?: typeof requestAnimationFrame;
  onClearPointsError?: (error: unknown) => void;
};

export const redrawDisplayForPointSizeChange = ({
  pcDisplay,
  pointSize,
  hoverModeRef,
  lastMousePositionRef,
  computeHoverAtClientPos,
  lastDrawnPcMessage,
  requestAnimationFrameFn = requestAnimationFrame,
  onClearPointsError = (error) => {
    console.warn('Error clearing points on point size change', error);
  },
}: PointSizeRedrawArgs): void => {
  const shouldResumeHover = hoverModeRef.current;
  pcDisplay.setHoverActive(false);
  pcDisplay.pointSize = pointSize;
  pcDisplay.resizePickingFramebuffer();

  try {
    pcDisplay.clearPoints();
  } catch (error) {
    onClearPointsError(error);
  }

  if (lastDrawnPcMessage) {
    pcDisplay.draw(lastDrawnPcMessage);
  }

  requestAnimationFrameFn(() => {
    if (shouldResumeHover) {
      pcDisplay.setHoverActive(true);
      const lastPos = lastMousePositionRef.current;
      if (lastPos) {
        computeHoverAtClientPos(lastPos.clientX, lastPos.clientY);
      }
    }
  });
};

type ZoomLifecycleArgs = {
  vizScene: ControlChangeAwareScene;
  getCameraShareState: () => CameraShareState | null;
  onCameraShareStateChange?: (state: CameraShareState) => void;
  lastDistanceRef: RefObject<number>;
  lastZoomRef: RefObject<number>;
  zoomTimeoutRef: RefObject<ReturnType<typeof setTimeout> | undefined>;
  isZoomingRef: RefObject<boolean>;
  setIsZooming: (value: boolean) => void;
  pcDisplayRef: RefObject<HoverActiveDisplay | undefined>;
  setHoveredPointData: (value: HoveredPointData | null) => void;
  hoverModeRef: RefObject<boolean>;
  setTimeoutFn?: typeof setTimeout;
  clearTimeoutFn?: typeof clearTimeout;
  zoomDebounceMs?: number;
};

export const setupControlsZoomLifecycle = ({
  vizScene,
  getCameraShareState,
  onCameraShareStateChange,
  lastDistanceRef,
  lastZoomRef,
  zoomTimeoutRef,
  isZoomingRef,
  setIsZooming,
  pcDisplayRef,
  setHoveredPointData,
  hoverModeRef,
  setTimeoutFn = setTimeout,
  clearTimeoutFn = clearTimeout,
  zoomDebounceMs = 150,
}: ZoomLifecycleArgs): (() => void) => {
  lastDistanceRef.current = vizScene.camera.position.distanceTo(
    vizScene.controls.target
  );
  lastZoomRef.current =
    typeof vizScene.camera.zoom === 'number' ? vizScene.camera.zoom : 1;

  const handleControlsChange = () => {
    const { camera, controls } = vizScene;
    if (!camera || !controls) return;

    const nextCameraState = getCameraShareState();
    if (nextCameraState) {
      onCameraShareStateChange?.(nextCameraState);
    }

    let isZoomChange = false;
    if (camera.isPerspectiveCamera) {
      const currentDistance = camera.position.distanceTo(controls.target);
      const distanceChange = Math.abs(
        currentDistance - lastDistanceRef.current
      );
      if (distanceChange > 0.01) {
        isZoomChange = true;
        lastDistanceRef.current = currentDistance;
      }
    } else if (camera.isOrthographicCamera) {
      const currentZoom = camera.zoom ?? 1;
      const zoomChange = Math.abs(currentZoom - lastZoomRef.current);
      if (zoomChange > 0.01) {
        isZoomChange = true;
        lastZoomRef.current = currentZoom;
      }
    }

    if (!isZoomChange) return;

    if (zoomTimeoutRef.current) {
      clearTimeoutFn(zoomTimeoutRef.current);
    }

    if (!isZoomingRef.current) {
      isZoomingRef.current = true;
      setIsZooming(true);
      pcDisplayRef.current?.setHoverActive(false);
      setHoveredPointData(null);
    }

    zoomTimeoutRef.current = setTimeoutFn(() => {
      isZoomingRef.current = false;
      setIsZooming(false);
      if (hoverModeRef.current) {
        pcDisplayRef.current?.setHoverActive(true);
      }
    }, zoomDebounceMs);
  };

  vizScene.controls.addEventListener('change', handleControlsChange);

  return () => {
    vizScene.controls.removeEventListener('change', handleControlsChange);
    if (zoomTimeoutRef.current) {
      clearTimeoutFn(zoomTimeoutRef.current);
      zoomTimeoutRef.current = undefined;
    }
  };
};

type PointerDragLifecycleArgs = {
  canvasTarget: PointerLifecycleEventTarget;
  pcDisplay: HoverActiveDisplay;
  hoverMode: boolean;
  isPointerDownRef: RefObject<boolean>;
  setIsPointerDown: (value: boolean) => void;
  setHoveredPointData: (value: HoveredPointData | null) => void;
  windowTarget?: PointerLifecycleEventTarget;
};

export const setupPointerDragLifecycle = ({
  canvasTarget,
  pcDisplay,
  hoverMode,
  isPointerDownRef,
  setIsPointerDown,
  setHoveredPointData,
  windowTarget = window,
}: PointerDragLifecycleArgs): (() => void) => {
  const onPointerDown = () => {
    isPointerDownRef.current = true;
    setIsPointerDown(true);
    pcDisplay.setHoverActive(false);
    setHoveredPointData(null);
  };

  const onPointerUpOrCancel = () => {
    isPointerDownRef.current = false;
    setIsPointerDown(false);
    if (hoverMode) {
      pcDisplay.setHoverActive(true);
    }
  };

  canvasTarget.addEventListener('pointerdown', onPointerDown);
  windowTarget.addEventListener('pointerup', onPointerUpOrCancel);
  windowTarget.addEventListener('pointercancel', onPointerUpOrCancel);
  windowTarget.addEventListener('pointerleave', onPointerUpOrCancel);

  return () => {
    windowTarget.removeEventListener('pointerup', onPointerUpOrCancel);
    windowTarget.removeEventListener('pointercancel', onPointerUpOrCancel);
    windowTarget.removeEventListener('pointerleave', onPointerUpOrCancel);
    canvasTarget.removeEventListener('pointerdown', onPointerDown);
  };
};

type HoverTrackingScene = Pick<VizScene, 'setHoverActive'>;

type HoverTrackingLifecycleArgs = {
  vizScene: HoverTrackingScene;
  pcDisplay: HoverActiveDisplay;
  hoverMode: boolean;
  isPointerDown: boolean;
  isSeeking: boolean;
  isZooming: boolean;
  lastMousePositionRef: RefObject<{
    clientX: number;
    clientY: number;
  } | null>;
  computeHoverAtClientPos: (clientX: number, clientY: number) => void;
  windowTarget?: PointerLifecycleEventTarget;
};

export const setupHoverTrackingLifecycle = ({
  vizScene,
  pcDisplay,
  hoverMode,
  isPointerDown,
  isSeeking,
  isZooming,
  lastMousePositionRef,
  computeHoverAtClientPos,
  windowTarget = window,
}: HoverTrackingLifecycleArgs): (() => void) => {
  if (!hoverMode || isPointerDown || isSeeking || isZooming) {
    return () => {};
  }

  vizScene.setHoverActive(hoverMode);
  pcDisplay.setHoverActive(hoverMode);

  const handleMouseMove = (event: MouseEvent) => {
    const { clientX, clientY } = event;

    lastMousePositionRef.current = { clientX, clientY };
    computeHoverAtClientPos(clientX, clientY);
  };

  windowTarget.addEventListener('mousemove', handleMouseMove);

  return () => {
    windowTarget.removeEventListener('mousemove', handleMouseMove);
  };
};

type PointCloudFrameUpdateArgs = {
  pcDisplay: PointCloudFrameDisplay | undefined;
  vizScene: Pick<VizScene, 'render'> | undefined;
  message: RangeRGBData[][] | null | undefined;
  didInitChannelsRef: RefObject<boolean>;
  initAvailableChannels: () => void;
  channelsResolved: boolean;
  pointSize: number;
  showPointCloud: boolean;
  whichReturnNumbers: number[];
  coloringMode: string;
  isDrawn: boolean;
  setIsDrawn: (value: boolean) => void;
  lastDrawnPcMessageRef: RefObject<RangeRGBData[][] | undefined>;
  isPausedLoopStoppedRef: RefObject<boolean>;
};

export const handlePointCloudFrameUpdate = ({
  pcDisplay,
  vizScene,
  message,
  didInitChannelsRef,
  initAvailableChannels,
  channelsResolved,
  pointSize,
  showPointCloud,
  whichReturnNumbers,
  coloringMode,
  isDrawn,
  setIsDrawn,
  lastDrawnPcMessageRef,
  isPausedLoopStoppedRef,
}: PointCloudFrameUpdateArgs): void => {
  if (!pcDisplay || !vizScene || !message) return;

  if (!didInitChannelsRef.current) {
    didInitChannelsRef.current = true;
    initAvailableChannels();
    return;
  }

  if (!channelsResolved) {
    return;
  }

  pcDisplay.pointSize = pointSize;
  pcDisplay.isVisible = showPointCloud;
  pcDisplay.whichReturns = whichReturnNumbers;
  pcDisplay.channelField = coloringMode;

  if (!isDrawn) {
    setIsDrawn(true);
  }
  lastDrawnPcMessageRef.current = message;
  pcDisplay.draw(message);

  if (isPausedLoopStoppedRef.current) {
    vizScene.render();
  }
};

export const applyCameraShareStateToVizScene = ({
  vizScene,
  state,
}: {
  vizScene: VizScene | undefined;
  state: CameraShareState;
}): void => {
  if (!vizScene) return;

  vizScene.setCameraType(state.cameraType === 'orthographic');
  vizScene.setCameraPose(state.position, state.target);

  if (
    state.cameraType === 'orthographic' &&
    vizScene.camera &&
    'zoom' in vizScene.camera
  ) {
    const orthoCamera = vizScene.camera as {
      zoom: number;
      updateProjectionMatrix: () => void;
    };
    orthoCamera.zoom = state.zoom;
    orthoCamera.updateProjectionMatrix();
  }

  vizScene.render();
};

export const refreshPointCloudLayout = ({
  vizScene,
  pcDisplay,
  lastDrawnPcMessage,
}: {
  vizScene: Pick<VizScene, 'render'> | undefined;
  pcDisplay:
    | Pick<PointcloudDisplay, 'resizePickingFramebuffer' | 'draw'>
    | undefined;
  lastDrawnPcMessage: RangeRGBData[][] | undefined;
}): void => {
  if (!vizScene || !pcDisplay) return;

  pcDisplay.resizePickingFramebuffer();
  if (lastDrawnPcMessage) {
    pcDisplay.draw(lastDrawnPcMessage);
  }
  vizScene.render();
};

/**
 * Main visualizer component
 * @param props - Chakra UI style props
 * panel
 * @returns div containing viz canvas
 */
export const VizPage = forwardRef<VizPageRef, VizPageProps>(
  (
    {
      hoverMode,
      showTransitionSpinner,
      onVizInitialized,
      onVizReadyChange,
      onCameraShareStateChange,
      ...props
    },
    ref
  ) => {
    const {
      driveInfo,
      pointSize,
      pointCloudFlipped,
      twoDImagesFlipped,
      isMobileViewport,
      pointCloudGlobalSettings,
      pointCloudSensorsSettings,
      paused,
    } = props;

    const threeDSceneCanvasRef = useRef<HTMLCanvasElement>(null);
    const threeDSceneDivRef = useRef<HTMLDivElement>(null);
    const [vizScene, setVizScene] = useState<VizScene>();
    const [pcDisplay, setPcDisplay] = useState<PointcloudDisplay>();
    const [vizInitError, setVizInitError] = useState(false);
    const [isDrawn, setIsDrawn] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [scaledOffset, setScaledOffset] = useState([0, 0]);
    const [containerState, setContainerState] = useState<
      ImageContainerState | undefined
    >(undefined);
    const imageContainerStateSensorRef = useRef<string | null>(null);
    const wasMobileViewportRef = useRef(isMobileViewport);
    const [hoveredPointData, setHoveredPointData] =
      useState<HoveredPointData | null>(null);
    const currentDataMessageRef = useRef<DataMessage[] | null>(null);
    const lastDrawnPcMessageRef = useRef<RangeRGBData[][] | undefined>(
      undefined
    );
    const isPointerDownRef = useRef(false);
    const [isPointerDown, setIsPointerDown] = useState(false);
    const isSeekingRef = useRef(false);
    const [isSeeking, setIsSeeking] = useState(false);
    const [isZooming, setIsZooming] = useState(false);
    const zoomTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
    const lastZoomRef = useRef<number>(1);
    const lastDistanceRef = useRef<number>(0);
    const hoverModeRef = useRef(hoverMode);
    const pcDisplayRef = useRef(pcDisplay);
    const isZoomingRef = useRef(false);
    const lastMousePositionRef = useRef<{
      clientX: number;
      clientY: number;
    } | null>(null);
    const pausedIdleTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
    const isPausedLoopStoppedRef = useRef(false);

    const pointCloudObservable = useStoreState(
      (store) => store.vizScene.pointCloudObservable
    );
    const is2DImageVisible = useStoreState(
      (store) => store.vizScene.is2DImageVisible
    );
    const showImageRulers = useStoreState(
      (store) => store.vizScene.twoDImageSettings.showImageRulers
    );
    const imageInfo = useStoreState(
      (store) => store.vizScene.twoDImageSettings.imageInfo
    );
    const imageObservables = useStoreState(
      (store) => store.vizScene.twoDImageSettings.imageObservables
    );
    const selectedImagePanelSn = useStoreState((store) =>
      selectSelectedImagePanelSn(store.vizScene)
    );
    const currentSensorImageSettings = useStoreState((store) =>
      selectSelectedSensorImageSettings(store.vizScene)
    );
    const dataMessage = useStoreState((store) => store.vizScene.dataMessage);
    const channelsResolved = useStoreState(
      (store) => store.vizScene.channelsResolved
    );
    const metadata = useStoreState(
      (store): SDKSensorInfoAugmented[] | undefined =>
        store.vizScene.metadata as SDKSensorInfoAugmented[] | undefined
    );
    const { setIs2DImageVisible, initAvailableChannels } = useStoreActions(
      (actions) => ({
        setIs2DImageVisible: actions.vizScene.setIs2DImageVisible,
        initAvailableChannels: actions.vizScene.initAvailableChannels,
      })
    );

    const {
      coloringMode,
      showPointCloud,
      isOrthographicCamera,
      showRangeRings,
      isAutoLevelsOn,
    } = pointCloudGlobalSettings;

    const selectedImageData = useMemo(
      () =>
        deriveSelectedImageData({
          channelsResolved,
          selectedImagePanelSn,
          imageInfo,
          imageObservables,
          currentSensorImageSettings,
        }),
      [
        channelsResolved,
        selectedImagePanelSn,
        imageInfo,
        imageObservables,
        currentSensorImageSettings,
      ]
    );

    const setContainerStateForCurrentSensor = useCallback(
      (newContainerState: ImageContainerState | undefined) => {
        imageContainerStateSensorRef.current = selectedImagePanelSn ?? null;
        setContainerState(newContainerState);
      },
      [selectedImagePanelSn]
    );

    const containerStateForPanel = deriveContainerStateForPanel(
      selectedImageData,
      imageContainerStateSensorRef.current,
      containerState
    );

    useEffect(() => {
      const wasMobileViewport = wasMobileViewportRef.current;
      wasMobileViewportRef.current = isMobileViewport;

      if (
        !shouldResetImagePanelContainerStateOnMobileViewportTransition({
          wasMobileViewport,
          isMobileViewport,
          containerState,
        })
      ) {
        return;
      }

      imageContainerStateSensorRef.current = null;
      setContainerState(undefined);
    }, [containerState, isMobileViewport]);

    const sensorSerialNumbers = useMemo(
      () => deriveSensorSerialNumbers(metadata, driveInfo),
      [metadata, driveInfo]
    );

    const whichReturnNumbers = useMemo(
      () =>
        deriveVisibleReturnNumbers(
          sensorSerialNumbers,
          pointCloudSensorsSettings
        ),
      [sensorSerialNumbers, pointCloudSensorsSettings]
    );

    const processDataMessages = useCallback(
      (messages: DataMessage[]) =>
        messages.map((dm) =>
          addRawRgbHoverChannels(
            applyPointCloudRgbAutoExposure(dm, coloringMode, isAutoLevelsOn),
            dm
          )
        ),
      [coloringMode, isAutoLevelsOn]
    );

    const computeHoverAtClientPos = useMemo(
      () => (clientX: number, clientY: number) => {
        if (!vizScene || !pcDisplay) return;
        if (!hoverModeRef.current) return;

        const hoverDataMessages = currentDataMessageRef.current;

        // skip hover if dragging or zooming
        if (isPointerDownRef.current || isZoomingRef.current) {
          setHoveredPointData(null);
          return;
        }

        if (!hoverDataMessages) {
          setHoveredPointData(null);
          return;
        }

        const hoveredPoint = resolveHoveredPoint(
          vizScene,
          pcDisplay,
          hoverDataMessages,
          clientX,
          clientY,
          {
            hoverMode: hoverModeRef.current,
            isPointerDown: isPointerDownRef.current,
            overPanelContainerId: 'container-image2d',
          }
        );

        setHoveredPointData(hoveredPoint);
      },
      [vizScene, pcDisplay]
    );

    // This UseEffect runs once, on startup
    // When pc div is available, create the viz scene and register event listeners
    useEffect(() => {
      // Scene creation (WebGL context acquisition, in particular) can fail for
      // reasons outside our control (GPU/driver restrictions, exhausted context
      // limits, etc). Once that happens it won't succeed on retry, so stop
      // trying and show a fallback instead of leaving the panel blank/crashed.
      if (vizInitError) return;

      try {
        const initializedResources = initializeVizSceneResources({
          vizScene,
          canvasElement: threeDSceneCanvasRef.current,
          overlayElement: threeDSceneDivRef.current,
          coloringMode,
        });

        if (initializedResources) {
          setPcDisplay(initializedResources.pcDisplay);
          setVizScene(initializedResources.scene);
        }
      } catch (err) {
        Sentry.captureException(err, {
          level: 'warning',
          tags: { section: 'VizPage' },
        });
        setVizInitError(true);
      }
    }, [coloringMode, vizScene, vizInitError]);

    useEffect(() => {
      onVizInitialized?.(Boolean(vizScene && pcDisplay));
    }, [vizScene, pcDisplay, onVizInitialized]);

    // Release the WebGL context when this scene is replaced or the component
    // unmounts (e.g. switching recordings). Without this, each mount leaks a
    // context, and enough leaked contexts eventually make the browser refuse
    // to create new ones at all.
    useEffect(() => {
      if (!vizScene) return undefined;
      return () => {
        vizScene.cancelRenderLoop();
        vizScene.cleanup();
      };
    }, [vizScene]);

    useEffect(() => {
      if (!vizScene) return;
      const nextCameraState: CameraShareState | null =
        cameraShareStateFromScene(vizScene);
      if (nextCameraState) {
        onCameraShareStateChange?.(nextCameraState);
      }
    }, [vizScene, onCameraShareStateChange]);

    useEffect(
      () => () => {
        onVizInitialized?.(false);
      },
      [onVizInitialized]
    );

    /* 
    Due to the compete, the point size is always set up with the proper value during drawing.
    we use this ref to make it when the canvas first get drawn, the point size is correct.
    So there will be no flickering of point size changing during loading. -- point size init moved to subscription
  */
    const didInitChannelsRef = useRef(false);

    // Subscription to pc observable
    // We need to resubscribe any time the pointcloud needs rerendering from a setting change
    useEffect(() => {
      const pointsSubscription = pointCloudObservable.subscribe(
        (message: RangeRGBData[][]) => {
          handlePointCloudFrameUpdate({
            pcDisplay,
            vizScene,
            message,
            didInitChannelsRef,
            initAvailableChannels,
            channelsResolved,
            pointSize,
            showPointCloud,
            whichReturnNumbers,
            coloringMode,
            isDrawn,
            setIsDrawn,
            lastDrawnPcMessageRef,
            isPausedLoopStoppedRef,
          });
        }
      );
      return () => {
        pointsSubscription.unsubscribe();
      };
    }, [
      pcDisplay,
      pointCloudObservable,
      vizScene,
      pointSize,
      showPointCloud,
      whichReturnNumbers,
      coloringMode,
      channelsResolved,
      initAvailableChannels,
      isDrawn,
    ]);

    const shouldShowInitialLoader = !isDrawn || !channelsResolved;

    useEffect(() => {
      onVizReadyChange?.(!shouldShowInitialLoader);
    }, [onVizReadyChange, shouldShowInitialLoader]);

    useEffect(
      () => () => {
        onVizReadyChange?.(false);
      },
      [onVizReadyChange]
    );

    useEffect(() => {
      const subscription = dataMessage.subscribe((messages) => {
        updateHoverStateAfterDataMessages({
          messages,
          processDataMessages,
          currentDataMessageRef,
          lastMousePositionRef,
          hoverModeRef,
          pcDisplayRef,
          computeHoverAtClientPos,
        });
      });

      return () => {
        subscription.unsubscribe();
      };
    }, [dataMessage, computeHoverAtClientPos, processDataMessages]);

    useEffect(() => {
      if (!pcDisplay || !vizScene) return;

      redrawDisplayForPointSizeChange({
        pcDisplay,
        pointSize,
        hoverModeRef,
        lastMousePositionRef,
        computeHoverAtClientPos,
        lastDrawnPcMessage: lastDrawnPcMessageRef.current,
      });
    }, [pointSize, pcDisplay, vizScene, computeHoverAtClientPos]);

    useEffect(() => {
      if (!vizScene || !pcDisplay) {
        return () => {};
      }

      return setupResizeDisplayLifecycle({
        vizScene,
        pcDisplay,
        hoverModeRef,
        lastMousePositionRef,
        computeHoverAtClientPos,
      });
    }, [vizScene, pcDisplay, computeHoverAtClientPos]);

    // Listen for pointcloud flip event
    useEffect(() => {
      updatePointCloudExtrinsics({
        pcDisplay,
        flipped: pointCloudFlipped,
        vizScene,
      });
    }, [pointCloudFlipped, pcDisplay, vizScene]);

    useEffect(() => {
      syncVizCameraType({
        vizScene,
        isOrthographicCamera,
        lastDistanceRef,
        lastZoomRef,
        onCameraShareStateChange,
        getCameraShareState: (scene) =>
          cameraShareStateFromScene(scene as unknown as VizScene),
      });
    }, [isOrthographicCamera, vizScene, onCameraShareStateChange]);

    useEffect(() => {
      if (!vizScene) return;
      vizScene.setRangeRingsVisibility(showRangeRings);
    }, [showRangeRings, vizScene]);

    useEffect(() => {
      if (
        !vizScene ||
        !hoverMode ||
        !pcDisplay ||
        isPointerDown ||
        isSeeking ||
        isZooming
      )
        return () => {};

      return setupHoverTrackingLifecycle({
        vizScene,
        pcDisplay,
        hoverMode,
        isPointerDown,
        isSeeking,
        isZooming,
        lastMousePositionRef,
        computeHoverAtClientPos,
      });
    }, [
      vizScene,
      hoverMode,
      pcDisplay,
      isPointerDown,
      isSeeking,
      isZooming,
      computeHoverAtClientPos,
    ]);

    useEffect(() => {
      hoverModeRef.current = hoverMode;
    }, [hoverMode]);
    useEffect(() => {
      pcDisplayRef.current = pcDisplay || undefined;
    }, [pcDisplay]);
    useEffect(() => {
      isZoomingRef.current = isZooming;
    }, [isZooming]);

    /* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call */
    useEffect(() => {
      if (!vizScene) return () => {};

      return setupControlsZoomLifecycle({
        vizScene,
        getCameraShareState: () => cameraShareStateFromScene(vizScene),
        onCameraShareStateChange,
        lastDistanceRef,
        lastZoomRef,
        zoomTimeoutRef,
        isZoomingRef,
        setIsZooming,
        pcDisplayRef,
        setHoveredPointData,
        hoverModeRef,
      });
    }, [vizScene, onCameraShareStateChange]);
    /* eslint-enable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call */
    useEffect(() => {
      if (!vizScene || !pcDisplay) return () => {};
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const canvasEl = vizScene.renderer.domElement as HTMLCanvasElement;

      return setupPointerDragLifecycle({
        canvasTarget: canvasEl,
        pcDisplay,
        hoverMode,
        isPointerDownRef,
        setIsPointerDown,
        setHoveredPointData,
      });
    }, [vizScene, pcDisplay, hoverMode]);

    useEffect(() => {
      if (pcDisplay) {
        pcDisplay.setHoverActive(hoverMode);
      }
    }, [hoverMode, pcDisplay]);

    useEffect(() => {
      if (!vizScene) return () => {};
      return setupPausedRenderLoopController({
        vizScene,
        paused,
        isPausedLoopStoppedRef,
        pausedIdleTimeoutRef,
      });
    }, [vizScene, paused]);

    /*
     * The 'real' aspect ratio of the 2D image we compute here is the
     * horizontal fov (360°) divided by the vertical fov (different for
     * every sensor model). And for some model, this ratio is fairly smaller
     * if we always try to place resize the panel based on width and ratio
     * it will cause the image panel take too many screen area.
     * Ideally, for a single image, it shouldn't take more than 25% vertical space.
     * Hence, we will do run below function once to auto resize panel at the beginning
     */
    const TwoDImageContainerRef = useRef<HTMLDivElement>(null);
    const isRatioChecked = useRef(false);
    useEffect(() => {
      if (isRatioChecked.current) return;
      const nextContainerState = deriveInitialImagePanelContainerState({
        containerElement: TwoDImageContainerRef.current,
        existingContainerState: containerState,
        screenHeight: window.innerHeight,
      });

      if (nextContainerState) {
        setContainerStateForCurrentSensor(nextContainerState);
        isRatioChecked.current = true;
      }
    }, [containerState, imageInfo, setContainerStateForCurrentSensor]);

    useImperativeHandle(
      ref,
      () => ({
        setSeekingState: (seeking: boolean) => {
          isSeekingRef.current = seeking;
          setIsSeeking(seeking);
          if (hoverMode && !isZooming) {
            // Re-enable hover when seeking stops
            if (pcDisplay) {
              pcDisplay.setHoverActive(true);
            }
          }
        },
        getIsDrawn: () => isDrawn,
        getCameraShareState: () => {
          const nextCameraState: CameraShareState | null =
            cameraShareStateFromScene(vizScene);
          return nextCameraState;
        },
        applyCameraShareState: (state: CameraShareState) => {
          applyCameraShareStateToVizScene({ vizScene, state });
        },
        refreshPointCloudLayout: () => {
          refreshPointCloudLayout({
            vizScene,
            pcDisplay,
            lastDrawnPcMessage: lastDrawnPcMessageRef.current,
          });
        },
        // Render and read the canvas in the same call stack - the
        // WebGLRenderer backing vizScene isn't created with
        // preserveDrawingBuffer, so a canvas read on a later tick (e.g.
        // after waiting for a state update) risks the browser having
        // already cleared the back buffer.
        captureFrame: () => {
          vizScene?.render();
          return threeDSceneCanvasRef.current?.toDataURL('image/png') ?? null;
        },
      }),
      [pcDisplay, hoverMode, isZooming, isDrawn, vizScene]
    );

    // Clear hover state when leaving the window; re-enable on re-entry
    useEffect(() => {
      if (!vizScene) return () => {};

      return setupHoverWindowLifecycle({
        hoverModeRef,
        lastMousePositionRef,
        pcDisplayRef,
        clearHoveredPointData: () => {
          setHoveredPointData(null);
        },
      });
    }, [vizScene]);

    return (
      <Grid
        placeItems="stretch"
        gridTemplateRows="1fr"
        position="relative"
        // Prevent floating panels from bleeding outside the viz area
        overflow="hidden"
        // Tomas: [3/12/2022] Maybe a hack. This was added here explicitly since
        // windows does not stretch the grid to full width otherwise
        w="100%"
      >
        <Box
          id="container-image2d"
          ref={TwoDImageContainerRef}
          position="absolute"
          inset={0}
          pointerEvents="none"
          visibility={shouldShowInitialLoader ? 'hidden' : 'visible'}
        >
          {is2DImageVisible && selectedImageData && (
            <TwoDImagePanel
              // Use data from the selectedImageData memo
              key={selectedImageData.sensorName}
              sensorName={selectedImageData.sensorName}
              images={selectedImageData.observables}
              imageAspectRatio={selectedImageData.info.aspectRatio}
              imageDims={selectedImageData.info.dimensions}
              onClose={() => {
                setIs2DImageVisible(false); // Action to hide the panel
              }}
              flipped={twoDImagesFlipped}
              showAngleRulers={showImageRulers}
              zoom={zoom}
              scaledOffset={scaledOffset}
              setZoom={setZoom}
              setScaledOffset={setScaledOffset}
              setContainerState={setContainerStateForCurrentSensor}
              containerState={containerStateForPanel}
            />
          )}
        </Box>
        <Box minH="0" flexGrow={1}>
          {vizInitError ? (
            <Flex w="100%" h="100%" alignItems="center" justifyContent="center">
              <Box
                lineHeight="2rem"
                textAlign="center"
                data-testid="viz-init-error-message"
              >
                Unable to display the 3D point cloud viewer in this browser.
                This may be due to a graphics driver or hardware limitation.
              </Box>
            </Flex>
          ) : (
            <>
              {shouldShowInitialLoader ? (
                <Flex
                  w="100%"
                  h="100%"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Spinner size="lg" label="Loading lidar viewer" />
                </Flex>
              ) : null}
              {!shouldShowInitialLoader && showTransitionSpinner ? (
                <Flex
                  w="100%"
                  h="100%"
                  alignItems="center"
                  justifyContent="center"
                  position="absolute"
                  inset={0}
                  pointerEvents="none"
                  zIndex="viz-area-overlay-2"
                >
                  <Spinner size="md" label="Loading frame" />
                </Flex>
              ) : null}
              <Box
                w="100%"
                h="100%"
                // Ref for CSS2DRenderer to render range ring labels
                position="absolute"
                ref={threeDSceneDivRef}
                pointerEvents="none"
                visibility={shouldShowInitialLoader ? 'hidden' : 'visible'}
              />
              <chakra.canvas
                ref={threeDSceneCanvasRef}
                w="100%"
                h="100%"
                visibility={shouldShowInitialLoader ? 'hidden' : 'visible'}
              />
              {!shouldShowInitialLoader &&
                hoverMode &&
                !isPointerDown &&
                !isZooming &&
                hoveredPointData && (
                  <PointCloudHoverTooltip
                    hoveredPointData={hoveredPointData}
                    isVisible
                  />
                )}
            </>
          )}
        </Box>
      </Grid>
    );
  }
);
